/** @typedef {import('../types/finance').RefundPreview} RefundPreview */
/** @typedef {import('../types/finance').Refund} Refund */
/** @typedef {import('../types/finance').DisputeResolution} DisputeResolution */
/** @typedef {import('../types/api').ApiResponse} ApiResponse */

import { instance } from '../config';

/**
 * Dry-run a refund to compute fees, conversion rate and customer payout.
 * Wallet details are optional. When supplied, the backend validates the wallet
 * against the refund currency/network and echoes the wallet address in preview.
 *
 * @param {{ reference: string, refund_type: string, wallet_address?: string, wallet_network?: string }} params
 * @returns {Promise<ApiResponse<RefundPreview>>}
 */
const previewRefund = async ({ reference, refund_type, wallet_address, wallet_network }) => {
  const { data } = await instance.post(`refunds/transactions/${reference}/preview`, {
    refund_type,
    ...(wallet_address ? { wallet_address } : {}),
    ...(wallet_network ? { wallet_network } : {})
  });
  return data;
};

/**
 * Create (initiate) a refund. Guarded by 2FA on the backend, so `otp` is
 * required. The `Idempotency-Key` header makes retries safe — generate one
 * per resolution flow and reuse it across retries.
 *
 * @param {{ reference: string, refund_type: string, wallet_address: string, wallet_network?: string, reason?: string, otp: string, idempotencyKey: string }} params
 * @returns {Promise<ApiResponse<Refund>>}
 */
const createRefund = async ({
  reference,
  refund_type,
  wallet_address,
  wallet_network,
  reason,
  otp,
  idempotencyKey
}) => {
  const { data } = await instance.post(
    `refunds/transactions/${reference}`,
    {
      refund_type,
      wallet_address,
      ...(wallet_network ? { wallet_network } : {}),
      ...(reason ? { reason } : {}),
      otp
    },
    { headers: { 'Idempotency-Key': idempotencyKey } }
  );
  return data;
};

/**
 * Accept a dispute (e.g. accept an overpayment) instead of refunding. Also
 * 2FA-guarded, so `otp` is required.
 *
 * @param {{ reference: string, action: string, otp: string, idempotencyKey: string }} params
 * @returns {Promise<ApiResponse<DisputeResolution>>}
 */
const acceptDispute = async ({ reference, action, otp, idempotencyKey }) => {
  const { data } = await instance.post(
    `disputes/${reference}/accept`,
    { action, otp },
    { headers: { 'Idempotency-Key': idempotencyKey } }
  );
  return data;
};

export { acceptDispute, createRefund, previewRefund };
